import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import SEOHead from "@/components/SEOHead";
import ScrollReveal from "@/components/ScrollReveal";
import HeroChatDemo from "@/components/HeroChatDemo";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Shield, Zap, Clock, FileX, MessageSquare, Globe, Search, Workflow, Users, Share2, CheckCircle, XCircle, Film, ArrowRight, PlayCircle } from "lucide-react";
import heroBg from "@/assets/hero-bg.jpg";
import marcusImg from "@/assets/testimonials/marcus.jpg";
import rachelImg from "@/assets/testimonials/rachel.jpg";
import davidImg from "@/assets/testimonials/david.jpg";
import { PremiumTestimonials } from "@/components/ui/premium-testimonials";

const Index = () => {
  const navigate = useNavigate();

  const scrollToHowItWorks = () => {
    document.getElementById("how-it-works")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <Layout>
      <SEOHead title="AI Chatbot Automation for Small Business | Xentic Solutions" description="Stop losing leads. Xentic Solutions builds AI chatbots that respond instantly, qualify leads, and book appointments. Live in 72 hours for just $150." path="/" />

      {/* HERO */}
      <section className="py-20 md:py-28 relative overflow-hidden">
        {/* Hero background image */}
        <div className="absolute inset-0">
          <img
            src={heroBg}
            alt=""
            width="1920"
            height="1080"
            fetchPriority="high"
            decoding="async"
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background/60 via-background/80 to-background" />
        </div>
        {/* Background depth */}
        <div className="absolute inset-0 grid-overlay" />
        <div className="absolute top-1/4 -left-32 w-96 h-96 bg-primary/20 rounded-full blur-[120px] animate-blob" />
        <div className="absolute bottom-1/4 -right-32 w-96 h-96 bg-accent/20 rounded-full blur-[120px] animate-blob-delay" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary/5 rounded-full blur-[100px]" />

        <div className="container relative z-10">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <ScrollReveal>
              <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-1.5 rounded-full text-sm font-medium mb-6 border border-primary/20">
                <span className="w-2 h-2 rounded-full bg-primary animate-pulse" />
                Now serving small businesses across 7+ industries
              </div>
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold leading-tight tracking-tight">
                Stop Losing Leads.<br />
                <span className="text-gradient">Start Automating.</span>
              </h1>
              <p className="mt-6 text-lg text-muted-foreground max-w-lg">
                Your competitors are closing leads while you sleep. Our AI chatbots capture, qualify, and convert leads 24/7 across your website, WhatsApp, social media, and SMS. Deploy in 72 hours and turn every conversation into revenue automatically.
              </p>
              <div className="mt-8 flex flex-wrap gap-4">
                <Button
                  size="lg"
                  className="border-0 hover:-translate-y-0.5 transition-transform duration-300 glow-shadow-sm"
                  style={{ background: "linear-gradient(135deg, hsl(189 100% 50%), hsl(243 76% 59%))" }}
                  onClick={() => navigate("/contact")}>

                  <span className="text-white font-semibold">Book Your Free Consultation →</span>
                </Button>
                <Button size="lg" variant="outline" className="border-border hover:bg-secondary hover:border-primary/30 transition-all" onClick={scrollToHowItWorks}>See How It Works ↓</Button>
              </div>
            </ScrollReveal>
            <ScrollReveal delay={0.2}>
              <HeroChatDemo />
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* TRUST STRIP */}
      <section className="border-y border-border section-alt py-6">
        <div className="container">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            {[
            { icon: Shield, text: "30-Day Money-Back Guarantee" },
            { icon: Zap, text: "$0 Upfront Risk" },
            { icon: Clock, text: "Live in 72 to 120 Hours" },
            { icon: FileX, text: "No Long-Term Contracts" }].
            map((t, i) =>
            <div key={i} className="flex flex-col items-center gap-2">
                <t.icon className="h-5 w-5 text-primary" />
                <span className="text-sm font-medium text-muted-foreground">{t.text}</span>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* STATS BAR */}
      <section className="py-12 relative overflow-hidden" style={{ background: "linear-gradient(135deg, hsl(189 100% 50%), hsl(243 76% 59%))" }}>
        <div className="container relative z-10">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-8 text-center">
            {[
            { stat: "40-60%", label: "More Leads Captured" },
            { stat: "72hrs", label: "Average Setup Time" },
            { stat: "$150", label: "One-Time Setup" },
            { stat: "7+", label: "Industries Served" },
            { stat: "30 Days", label: "Risk-Free Trial" }].
            map((s, i) =>
            <div key={i}>
                <div className="text-3xl font-heading font-bold text-white">{s.stat}</div>
                <div className="text-sm mt-1 text-white/70">{s.label}</div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* PROBLEM SECTION */}
      <section className="py-20">
        <div className="container">
          <ScrollReveal>
            <p className="text-center text-sm uppercase tracking-widest text-muted-foreground mb-3">Common Problems</p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-center mb-12">Sound Familiar?</h2>
          </ScrollReveal>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
            { title: "Missing Leads", desc: "Customers message after hours and never hear back. They move on to someone who responds.", icon: MessageSquare },
            { title: "Repeating Yourself", desc: "Answering the same questions over and over wastes hours you could spend growing your business.", icon: Workflow },
            { title: "Paying Too Much Staff", desc: "Hiring receptionists or support agents just to answer basic questions is expensive and inefficient.", icon: Users },
            { title: "Inconsistent Follow-Up", desc: "Leads slip through the cracks because there's no system to follow up automatically.", icon: Search }].
            map((p, i) =>
            <ScrollReveal key={i} delay={i * 0.1}>
                <Card className="h-full">
                  <CardContent className="p-6">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                      <p.icon className="h-5 w-5 text-primary" />
                    </div>
                    <h3 className="font-heading font-bold text-lg mb-2">{p.title}</h3>
                    <p className="text-sm text-muted-foreground">{p.desc}</p>
                  </CardContent>
                </Card>
              </ScrollReveal>
            )}
          </div>
        </div>
      </section>

      {/* FIX THIS TODAY CTA */}
      <section className="py-12 section-alt">
        <div className="container text-center">
          <ScrollReveal>
            <h2 className="font-heading text-2xl md:text-3xl font-bold mb-4">Ready to Fix This?</h2>
            <p className="text-muted-foreground mb-6">Let us show you how AI automation can transform your business in days, not months.</p>
            <Button
              size="lg"
              className="border-0 hover:-translate-y-0.5 transition-transform duration-300"
              style={{ background: "linear-gradient(135deg, hsl(189 100% 50%), hsl(243 76% 59%))" }}
              onClick={() => navigate("/contact")}>

              <span className="text-white font-semibold">Fix This Today →</span>
            </Button>
          </ScrollReveal>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how-it-works" className="py-20">
        <div className="container">
          <ScrollReveal>
            <p className="text-center text-sm uppercase tracking-widest text-muted-foreground mb-3">Process</p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-center mb-12">How It Works</h2>
          </ScrollReveal>
          <div className="grid md:grid-cols-3 gap-8">
            {[
            { step: "1", title: "Book a Free Consultation", desc: "Tell us about your business. We'll map out exactly how AI automation fits your workflow." },
            { step: "2", title: "We Build It For You", desc: "Our team builds, trains, and deploys your custom AI chatbot tailored to your industry." },
            { step: "3", title: "Go Live & Grow", desc: "Your chatbot goes live within 72 hours. Watch leads come in while you focus on what matters." }].
            map((s, i) =>
            <ScrollReveal key={i} delay={i * 0.15}>
                <Card className="text-center h-full">
                  <CardContent className="p-8">
                    <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto font-heading font-bold text-xl mb-4 text-white" style={{ background: "linear-gradient(135deg, hsl(189 100% 50%), hsl(243 76% 59%))" }}>{s.step}</div>
                    <h3 className="font-heading font-bold text-lg mb-2">{s.title}</h3>
                    <p className="text-sm text-muted-foreground">{s.desc}</p>
                  </CardContent>
                </Card>
              </ScrollReveal>
            )}
          </div>
        </div>
      </section>

      {/* SERVICES OVERVIEW */}
      <section className="py-20 section-alt">
        <div className="container">
          <ScrollReveal>
            <p className="text-center text-sm uppercase tracking-widest text-muted-foreground mb-3">Solutions</p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-center mb-12">What We Do</h2>
          </ScrollReveal>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
            { icon: MessageSquare, title: "AI Chatbot Automation", result: "Capture 40 to 60% more leads automatically" },
            { icon: Globe, title: "Website Development", result: "High-converting sites built for performance" },
            { icon: Search, title: "SEO Optimization", result: "Rank higher and get found by local customers" },
            { icon: Workflow, title: "AI Workflow Automation", result: "Eliminate repetitive tasks and save hours weekly" },
            { icon: Users, title: "Lead Generation Systems", result: "Consistent pipeline of qualified prospects" },
            { icon: Share2, title: "Social Media Management", result: "Stay top of mind without lifting a finger" }].
            map((s, i) =>
            <ScrollReveal key={i} delay={i * 0.1}>
                <Card
                  className="h-full cursor-pointer transition-all duration-300 hover:-translate-y-1 hover:border-primary/40 hover:shadow-[0_0_30px_-10px_hsl(189_100%_50%/0.4)]"
                  onClick={() => navigate("/services")}
                  role="link"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === "Enter") navigate("/services"); }}
                >
                  <CardContent className="p-6">
                    <s.icon className="h-8 w-8 text-primary mb-4" />
                    <h3 className="font-heading font-bold text-lg mb-2">{s.title}</h3>
                    <p className="text-sm text-primary font-medium">{s.result}</p>
                    <div className="mt-4 inline-flex items-center gap-1 text-xs text-muted-foreground group-hover:text-primary">
                      Learn more <ArrowRight className="h-3 w-3" />
                    </div>
                  </CardContent>
                </Card>
              </ScrollReveal>
            )}
          </div>

          {/* Video Editing Portfolio CTA */}
          <ScrollReveal>
            <a
              href="https://video-editing.xenticsolutions.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="group mt-10 block rounded-2xl border border-primary/20 glass-card p-6 md:p-8 hover:border-primary/50 hover:-translate-y-0.5 transition-all duration-300 hover:shadow-[0_0_40px_-10px_hsl(189_100%_50%/0.5)]"
            >
              <div className="flex flex-col md:flex-row items-start md:items-center gap-5 md:gap-6">
                <div
                  className="w-14 h-14 rounded-2xl flex items-center justify-center shrink-0"
                  style={{ background: "linear-gradient(135deg, hsl(189 100% 50%), hsl(243 76% 59%))" }}
                >
                  <Film className="h-7 w-7 text-white" />
                </div>
                <div className="flex-1">
                  <div className="inline-flex items-center gap-2 text-xs font-medium text-primary mb-2">
                    <PlayCircle className="h-3.5 w-3.5" /> New
                  </div>
                  <h3 className="font-heading font-bold text-xl md:text-2xl mb-1">Video Editing Portfolio</h3>
                  <p className="text-sm text-muted-foreground">Explore our latest reels, ads, and creative cuts crafted for brands.</p>
                </div>
                <span
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold text-white shrink-0 group-hover:gap-3 transition-all"
                  style={{ background: "linear-gradient(135deg, hsl(189 100% 50%), hsl(243 76% 59%))" }}
                >
                  View Portfolio <ArrowRight className="h-4 w-4" />
                </span>
              </div>
            </a>
          </ScrollReveal>
        </div>
      </section>

      {/* COMPARISON TABLE */}
      <section className="py-20">
        <div className="container max-w-3xl">
          <ScrollReveal>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-center mb-12">Why Choose Xentic?</h2>
          </ScrollReveal>
          <ScrollReveal>
            <div className="rounded-xl overflow-hidden glass-card -mx-2 md:mx-0">
              <table className="w-full text-xs md:text-sm">
                <thead>
                  <tr style={{ background: "linear-gradient(135deg, hsl(189 100% 50%), hsl(243 76% 59%))" }}>
                    <th className="text-left p-2 md:p-4 text-white">Feature</th>
                    <th className="text-center p-2 md:p-4 text-white">Xentic</th>
                    <th className="text-center p-2 md:p-4 text-white">Competitors</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                  ["Setup Cost", "$150", "$2,000 to $10,000+"],
                  ["Setup Time", "72 to 120 hours", "2 to 6 weeks"],
                  ["Monthly Cost", "monthly_special", "$200 to $1,000/mo"],
                  ["Money-Back Guarantee", true, false],
                  ["Custom AI Training", true, false],
                  ["No Long-Term Contracts", true, false],
                  ["Transparent Pricing", true, false]].
                  map(([feature, xentic, comp], i) =>
                  <tr key={i} className={i % 2 === 0 ? "bg-secondary/30" : ""}>
                      <td className="p-2 md:p-4 font-medium">{feature as string}</td>
                      <td className="p-2 md:p-4 text-center">
                        {typeof xentic === "boolean" ?
                      <CheckCircle className="h-5 w-5 text-green-400 mx-auto" /> :
                      xentic === "monthly_special" ? <span className="font-semibold text-primary text-center"><span className="block">$50/month</span><span className="block text-xs text-muted-foreground font-normal mt-0.5">Optional after 3 months. Includes dedicated support.</span></span> :
                      <span className="font-semibold text-primary">{xentic}</span>
                      }
                      </td>
                      <td className="p-2 md:p-4 text-center">
                        {typeof comp === "boolean" ?
                      <XCircle className="h-5 w-5 text-destructive mx-auto" /> :
                      <span className="text-muted-foreground">{comp}</span>
                      }
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </ScrollReveal>
        </div>
      </section>

      <PremiumTestimonials
        className="section-alt"
        testimonials={[
          {
            name: "Marcus Thompson",
            role: "Owner, Thompson Heating & Air",
            company: "Thompson HVAC",
            avatar: marcusImg,
            rating: 5,
            text: "Before Xentic, we were losing about 60% of our after-hours leads. Now the chatbot picks up every single inquiry, qualifies them, and books the service call. It paid for itself in the first three days.",
            results: ["+58% leads captured", "3-day payback", "24/7 coverage"]
          },
          {
            name: "Rachel Kim",
            role: "Practice Manager, BrightSmile Dental",
            company: "BrightSmile Dental",
            avatar: rachelImg,
            rating: 5,
            text: "Our front desk was overwhelmed with appointment requests and basic questions. The AI handles all of that now, and patients actually prefer it because they get instant responses at any hour.",
            results: ["+45% bookings", "Zero wait time", "Happier patients"]
          },
          {
            name: "David Alvarez",
            role: "Managing Partner, Alvarez Law Group",
            company: "Alvarez Law Group",
            avatar: davidImg,
            rating: 5,
            text: "We only want to talk to people who are ready to hire. The chatbot screens every lead, asks the right questions, and only sends us qualified prospects. Our conversion rate went through the roof.",
            results: ["+62% qualified leads", "Smarter screening", "Higher close rate"]
          },
          {
            name: "Sarah Mitchell",
            role: "Real Estate Agent, Keller Williams",
            company: "Keller Williams",
            avatar: "https://images.unsplash.com/photo-1580489944761-15a19d654956?w=150&h=150&fit=crop&crop=face",
            rating: 5,
            text: "Weekends and evenings used to be dead zones for lead capture. Now my AI assistant books showings around the clock and my pipeline has never been this full. Did not have to hire anyone.",
            results: ["Weekend leads captured", "Auto-booked showings", "No extra staff"]
          },
          {
            name: "James Liu",
            role: "Founder, UrbanCart E-Commerce",
            company: "UrbanCart",
            avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face",
            rating: 5,
            text: "Support tickets dropped by 70% after we set up the chatbot. It handles returns, order tracking, and product questions without breaking a sweat. Our team finally has time to focus on growth.",
            results: ["-70% support tickets", "Instant answers", "Team freed up"]
          }
        ]}
      />

      {/* INDUSTRIES */}
      <section className="py-20">
        <div className="container text-center">
          <ScrollReveal>
            <p className="text-sm uppercase tracking-widest text-muted-foreground mb-3">Industries</p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-8">Industries We Serve</h2>
            <div className="flex flex-wrap justify-center gap-3">
              {["HVAC", "Healthcare", "Legal", "Real Estate", "E-Commerce", "Education", "Hospitality", "Accounting"].map((ind) =>
              <span key={ind} className="glass-card px-5 py-2 rounded-full text-sm font-medium hover:bg-primary/10 hover:text-primary hover:border-primary/30 transition-all duration-300 cursor-default">
                  {ind}
                </span>
              )}
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* PRICING TEASER */}
      <section className="py-20 section-alt">
        <div className="container max-w-2xl text-center">
          <ScrollReveal>
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">Simple, Honest Pricing</h2>
            <p className="text-muted-foreground mb-2">One-time setup: <span className="font-bold text-primary text-2xl">$150</span></p>
            <p className="text-muted-foreground mb-6">3 months free. Optional Support Manager $50 per month. No hidden fees. No contracts.</p>
            <Button
              size="lg"
              className="border-0 hover:-translate-y-0.5 transition-transform duration-300"
              style={{ background: "linear-gradient(135deg, hsl(189 100% 50%), hsl(243 76% 59%))" }}
              onClick={() => navigate("/pricing")}>

              <span className="text-white font-semibold">See Full Pricing Breakdown →</span>
            </Button>
          </ScrollReveal>
        </div>
      </section>

      {/* BOTTOM CTA */}
      <section className="py-20 relative overflow-hidden" style={{ background: "linear-gradient(135deg, rgba(0,217,255,0.08), rgba(79,70,229,0.08))" }}>
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-primary/10 rounded-full blur-[120px]" />
        <div className="container text-center relative z-10">
          <ScrollReveal>
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">Every Day Without Automation Is Revenue Walking Out the Door</h2>
            <p className="text-muted-foreground mb-8 max-w-xl mx-auto">Your competitors are already automating. Don't let another lead slip through the cracks.</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
              <Button
                size="lg"
                className="border-0 hover:-translate-y-0.5 transition-transform duration-300 glow-shadow-sm"
                style={{ background: "linear-gradient(135deg, hsl(189 100% 50%), hsl(243 76% 59%))" }}
                onClick={() => navigate("/contact")}>

                <span className="text-white font-semibold">Book Free Consultation →</span>
              </Button>
              <a

                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center w-12 h-12 rounded-full border border-border bg-secondary/50 hover:border-green-400/40 hover:bg-green-500/10 hover:-translate-y-0.5 transition-all duration-300" href="https://wa.me/19178070944">

                <svg viewBox="0 0 24 24" className="h-5 w-5 fill-green-400"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" /></svg>
                <span className="sr-only">Chat on WhatsApp</span>
              </a>
            </div>
          </ScrollReveal>
        </div>
      </section>
    </Layout>);

};

export default Index;